import React from 'react';
import { useSelector } from 'react-redux';

// Types
import { QuestionTypeType, QuestionVisibilityType } from 'Shared/types/ReviewFormTemplate.type';

// Helpers
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';
import * as UserHelpers from 'Shared/model_helpers/User.helpers';

// Components
import AdjectivesForPropertyQuestion from './AdjectivesForPropertyQuestion';
import GifQuestion from './GifQuestion';
import TextareaQuestion from './TextareaQuestion';
import RatingQuestion from './RatingQuestion';
import QuestionIsNotForUser from './QuestionIsNotForUser';
import TitleDescriptionSection from './TitleDescriptionSection';

// Selectors
import * as UserSelectors from 'Shared/selectors/Users.selectors';

type Props = {
  allPeerReviewForms?;
  receiverSlug: string;
  question;
  isOpen: boolean;
  handleMoveNext?: () => void;
  handleMoveBack?: () => void;
  answers;
  formData;
  handleSetAnswers: Function;
  isPreview?: boolean;
};

const FormQuestion = ({
  allPeerReviewForms,
  receiverSlug,
  question,
  isOpen,
  handleMoveNext,
  handleMoveBack,
  answers,
  formData,
  handleSetAnswers,
  isPreview = false,
}: Props) => {
  const questionType = ReviewFormTemplatesHelpers.getQuestionType(question);
  const questionVisibility = ReviewFormTemplatesHelpers.getQuestionVisibility(question);

  const receiver = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: receiverSlug }));
  const currentUserFirstName = useSelector(state => state.user.first_name);
  const receiverName = !isPreview ? UserHelpers.getFirstName(receiver) : currentUserFirstName;
  const currentUserSlug = useSelector(state => state.user.slug);
  const isSelfReview = !isPreview ? receiverSlug === currentUserSlug : false;

  const peerReviewFormAnswers = allPeerReviewForms
    ? allPeerReviewForms
        .valueSeq()
        .map(reviewForm => ReviewFormHelpers.getAnswers(reviewForm))
        .filter(answer => answer)
        .toJS()
    : [];

  if (
    (questionVisibility === QuestionVisibilityType.SELF_REVIEW && !isSelfReview) ||
    (questionVisibility === QuestionVisibilityType.NON_SELF_REVIEW && isSelfReview)
  )
    return (
      <QuestionIsNotForUser
        question={question}
        isOpen={isOpen}
        handleMoveNext={handleMoveNext}
        handleMoveBack={handleMoveBack}
        answers={answers}
        handleSetAnswers={handleSetAnswers}
      />
    );

  switch (questionType) {
    case QuestionTypeType.TITLE_DESCRIPTION:
      return (
        <TitleDescriptionSection
          question={question}
          isOpen={isOpen}
          handleMoveNext={handleMoveNext}
          handleMoveBack={handleMoveBack}
        />
      );
    case QuestionTypeType.ADJECTIVES_FOR_PROPERTY:
      return (
        <AdjectivesForPropertyQuestion
          receiverName={receiverName}
          question={question}
          isOpen={isOpen}
          handleMoveNext={handleMoveNext}
          handleMoveBack={handleMoveBack}
          answers={answers}
          handleSetAnswers={handleSetAnswers}
          isPreview={isPreview}
        />
      );
    case QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY:
      return (
        <AdjectivesForPropertyQuestion
          receiverName={receiverName}
          peerReviewFormAnswers={peerReviewFormAnswers}
          question={question}
          isOpen={isOpen}
          handleMoveNext={handleMoveNext}
          handleMoveBack={handleMoveBack}
          answers={answers}
          handleSetAnswers={handleSetAnswers}
          isSummaryQuestion
          isPreview={isPreview}
        />
      );
    case QuestionTypeType.GIF:
      return (
        <GifQuestion
          receiverName={receiverName}
          question={question}
          isOpen={isOpen}
          handleMoveNext={handleMoveNext}
          handleMoveBack={handleMoveBack}
          answers={answers}
          handleSetAnswers={handleSetAnswers}
          isPreview={isPreview}
        />
      );
    case QuestionTypeType.HOLISTIC_SUMMARY:
    case QuestionTypeType.TEXTAREA:
      return (
        <TextareaQuestion
          receiverName={receiverName}
          question={question}
          isOpen={isOpen}
          handleMoveNext={handleMoveNext}
          handleMoveBack={handleMoveBack}
          answers={answers}
          formData={formData}
          handleSetAnswers={handleSetAnswers}
          isPreview={isPreview}
          isSelfReview={isSelfReview}
        />
      );
    case QuestionTypeType.RATING:
      return (
        <RatingQuestion
          receiverName={receiverName}
          question={question}
          isOpen={isOpen}
          handleMoveNext={handleMoveNext}
          handleMoveBack={handleMoveBack}
          answers={answers}
          handleSetAnswers={handleSetAnswers}
          isPreview={isPreview}
          isSelfReview={isSelfReview}
        />
      );
    case QuestionTypeType.RATING_SUMMARY:
      return (
        <RatingQuestion
          receiverName={receiverName}
          question={question}
          isOpen={isOpen}
          handleMoveNext={handleMoveNext}
          handleMoveBack={handleMoveBack}
          answers={answers}
          formData={formData}
          handleSetAnswers={handleSetAnswers}
          isSummaryQuestion
          isPreview={isPreview}
        />
      );
    default:
      return null;
  }
};

export default FormQuestion;
